/**
 * Single source of truth for what files Ideoz accepts as attachments
 * (startup files, scope files, in-chat uploads, Google Drive picker, etc.).
 *
 * To add or remove an allowed extension, edit only this file. Every UI dialog
 * and helper consumes these constants — no parallel allow-lists elsewhere.
 */

export const MAX_ATTACHMENT_SIZE_MB = 10;
export const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

/**
 * Allowed file extensions, lowercase, no leading dot. Ordered roughly by how
 * we'd describe them to a user (text → office docs → images).
 */
export const ALLOWED_ATTACHMENT_EXTENSIONS = [
  'txt',
  'csv',
  'md',
  'pdf',
  'docx',
  'doc',
  'pptx',
  'ppt',
  'xlsx',
  'xls',
  'jpg',
  'jpeg',
  'png',
  'bmp',
  'tiff',
  'tif',
  'gif',
  'webp',
] as const;

export type AllowedAttachmentExtension = (typeof ALLOWED_ATTACHMENT_EXTENSIONS)[number];

/**
 * MIME types that map to the allowed extensions above. Used by `<input
 * type="file" accept="…">` and by MIME-typed allow-list checks (legacy
 * UploadFilesDialog still checks `file.type`).
 *
 * `image/jpg` is non-standard but some browsers report it for .jpg files
 * — include it so we don't reject those files.
 *
 * This set is kept in lockstep with the AI service's `ALLOWED_MIME` — the AI
 * rejects anything outside it, so we must not offer types it can't process.
 * SVG is accepted in chat only: see `CHAT_ATTACHMENT_MIME_TYPES`.
 */
export const ALLOWED_ATTACHMENT_MIME_TYPES = [
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  // Legacy OLE compound files (some browsers report old .doc/.xls/.ppt as this).
  'application/x-ole-storage',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/bmp',
  'image/tiff',
  'image/gif',
  'image/webp',
] as const;

export type AllowedAttachmentMimeType = (typeof ALLOWED_ATTACHMENT_MIME_TYPES)[number];

/** Comma-separated string for `<input type="file" accept="…">`. */
export const ALLOWED_ATTACHMENT_ACCEPT_ATTR = ALLOWED_ATTACHMENT_EXTENSIONS.map(
  (ext) => `.${ext}`
).join(',');

/**
 * Image-only vs document-only `accept` subsets — used by the composer's "Upload
 * images" / "Upload files" menu entries to scope the native file picker. Both
 * derive from `ALLOWED_ATTACHMENT_EXTENSIONS` so this stays the single allow-list.
 */
export const IMAGE_ATTACHMENT_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'bmp',
  'tiff',
  'tif',
  'gif',
  'webp',
] as const;

export const ALLOWED_IMAGE_ACCEPT_ATTR = ALLOWED_ATTACHMENT_EXTENSIONS.filter((ext) =>
  (IMAGE_ATTACHMENT_EXTENSIONS as readonly string[]).includes(ext)
)
  .map((ext) => `.${ext}`)
  .join(',');

export const ALLOWED_DOC_ACCEPT_ATTR = ALLOWED_ATTACHMENT_EXTENSIONS.filter(
  (ext) => !(IMAGE_ATTACHMENT_EXTENSIONS as readonly string[]).includes(ext)
)
  .map((ext) => `.${ext}`)
  .join(',');

/**
 * The chat accepts everything above plus SVG, the usual format of a founder's logo
 * (User Story 770). The AI service's chat upload takes it and describes it from its
 * name; project and scope uploads still refuse it, so it stays out of the lists above.
 */
export const CHAT_ONLY_ATTACHMENT_EXTENSIONS = ['svg'] as const;

export const CHAT_ATTACHMENT_MIME_TYPES = [
  ...ALLOWED_ATTACHMENT_MIME_TYPES,
  'image/svg+xml',
] as const;

export const CHAT_ATTACHMENT_ACCEPT_ATTR = [
  ALLOWED_ATTACHMENT_ACCEPT_ATTR,
  ...CHAT_ONLY_ATTACHMENT_EXTENSIONS.map((ext) => `.${ext}`),
].join(',');

export const CHAT_IMAGE_ACCEPT_ATTR = [
  ALLOWED_IMAGE_ACCEPT_ATTR,
  ...CHAT_ONLY_ATTACHMENT_EXTENSIONS.map((ext) => `.${ext}`),
].join(',');

/** Friendly upper-cased list for display, e.g. in dialog helper text. */
export const ALLOWED_ATTACHMENT_DISPLAY_LIST = ALLOWED_ATTACHMENT_EXTENSIONS.map((ext) =>
  ext.toUpperCase()
).join(', ');

/** Pretty extension display + size cap — used in the "Maximum file size 5MB" line. */
export function getSupportedAttachmentTypesString(): string {
  return ALLOWED_ATTACHMENT_DISPLAY_LIST;
}

/** Lowercase extension (no dot) from a file name, '' if none. */
export function getFileExtension(name: string): string {
  const idx = name.lastIndexOf('.');
  if (idx < 0) return '';
  return name.slice(idx + 1).toLowerCase();
}

/** True if the extension on `name` is in the allow-list. */
export function isAllowedAttachmentName(name: string): boolean {
  const ext = getFileExtension(name);
  return (ALLOWED_ATTACHMENT_EXTENSIONS as readonly string[]).includes(ext);
}

/** True if the extension on `name` can be sent in chat (the allow-list plus SVG). */
export function isAllowedChatAttachmentName(name: string): boolean {
  return (
    isAllowedAttachmentName(name) ||
    (CHAT_ONLY_ATTACHMENT_EXTENSIONS as readonly string[]).includes(getFileExtension(name))
  );
}

/** True if the MIME `type` is in the allow-list. */
export function isAllowedAttachmentMime(type: string): boolean {
  return (ALLOWED_ATTACHMENT_MIME_TYPES as readonly string[]).includes(type);
}

/** True if the file size is within the limit. */
export function isAttachmentSizeAllowed(bytes: number): boolean {
  return bytes <= MAX_ATTACHMENT_SIZE_BYTES;
}
